import { createSelector } from 'reselect';

import {
  CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID,
  DISABLE_SHIPMENT_TRACKING,
  EXPERIENCE_ID,
  HAS_LEGACY_CLAIM_PAGE_CONFIG,
  INTERNAL_NOTES,
  PRE_CREATED_ENGAGEMENT_ID,
  RECEIVER_REMINDER_INTERVAL,
  SEND_ON_BEHALF_THE_EMAIL,
  SEND_ON_HOLD,
} from '../../constants/bucket';
import { BUSINESS_REASON_REQUIRED, DISABLE_HANDWRITTEN_NOTE_STEP } from '../../constants/campaigns';
import { OFF } from '../../constants/integrations';
import { TITLE } from '../../constants/oneLink';
import { REQUIRE_BUSINESS_REASON_FOR_DIRECT_SENDS } from '../../constants/organizationsKeys';
import { BUSINESS_REASON_ID } from '../../constants/reasons';
import { URL_VARS } from '../../constants/routing';
import {
  ALLOWED_DOMAINS,
  AWAITING_ADDRESS,
  DISABLE_RECIPIENT_SHIPPED_EMAIL,
  DISABLE_SENDER_EMAIL_NOTIFICATIONS,
  ENABLED_2FA,
  getShippingInitialValues,
  getShippingValidationSchema,
  INITIAL_ONE_LINK_DIGITAL_SHIPPING_FORM_STATE,
  INITIAL_ONE_LINK_PHYSICAL_SHIPPING_FORM_STATE,
  INITIAL_SHIPPING_ADDRESS_FORM_STATE,
  ONE_LINK_DIGITAL_SHIPPING_VALIDATION_SCHEMA,
  ONE_LINK_PHYSICAL_SHIPPING_VALIDATION_SCHEMA,
  RECEIVER_CRM_RECORD_ID,
  RECIPIENTS,
  SENDER_COMPANY_NAME,
  STATUS,
} from '../../constants/shipping';
import { SIGNATURE } from '../../constants/signature';
import {
  DIGITAL_GIFT_NAMES,
  EMAIL_MESSAGE,
  EMAIL_SUBJECT,
  EMAIL_TEMPLATE_ID,
  MESSAGE,
  NOTE_TEMPLATE_ID,
  RECEIVER_EMAIL,
} from '../../constants/templates';
import { ShippingDigitalAddressCandidate } from '../../types/address';
import { IOneLinkEngagementCandidate, TEngagementCandidate } from '../../types/bucket';
import { ICampaign, ICampaignItem } from '../../types/campaigns';
import { IInventoryWithType, IOnDemandItem } from '../../types/inventories';
import { EngagementReminderIntervalsEnum, EngagementSourceTypesEnum } from '../../types/reports';
import { mapShippingDetailsToSend } from '../../utils/bucket';
import { isDigitalBox } from '../../utils/campaigns';
import { isItemDigital } from '../../utils/inventories';
import { mapShippingDetailsToOneLink } from '../../utils/oneLink';
import { getSenderInfoObject } from '../../utils/users';
import { IAppState } from '../reducers';
import { selectIsCrmSkipAllowedToUser, selectUserEmail, selectUserInfo } from './auth';
import { selectFulfillmentCenterItems } from './fulfillmentCenters';
import {
  selectCurrentOrganizationId,
  selectCurrentOrganizationName,
  selectCurrentOrganizationOptions,
  selectIsCurrentOrganizationSupportedAnyCrm,
  selectIsSkipCrmAllowedInOrg,
} from './organizations';
import { selectCurrentSignature } from './signature';
import { selectUsers } from './users';

export const selectRootState = (state: IAppState) => state.bucket;

export const selectBucketItems = createSelector(selectRootState, (bucket) => (bucket ? bucket.items : undefined));

export const selectCurrentFlow = createSelector(selectRootState, (bucket) => (bucket ? bucket.flow : undefined));

export const selectShouldStartFromScratch = (flowId: string | undefined) =>
  createSelector(selectCurrentFlow, (flow) => flow !== flowId);

export const selectIsOneLinkFlow = createSelector(selectCurrentFlow, (flowId) => flowId === URL_VARS.ONE_LINK);

export const selectIsDirectSendFlow = createSelector(selectCurrentFlow, (flowId) => flowId === URL_VARS.DIRECT_SEND);

export const selectPYGCampaignsList = createSelector(selectRootState, (bucket) =>
  bucket ? bucket.pick_campaigns : undefined,
);

export const selectPYGCampaignsIDsList = createSelector(selectPYGCampaignsList, (campaignsList) =>
  campaignsList ? campaignsList.map(({ box_id }) => box_id) : undefined,
);

export const selectIsPYGEnabled = createSelector(selectPYGCampaignsList, (ids) => Array.isArray(ids));

export const selectIsPYGCampaignsDigital = createSelector(selectPYGCampaignsList, (campaignsList) =>
  campaignsList?.every(isDigitalBox),
);

export const selectPreCreatedBoxId = createSelector(selectRootState, (bucket) =>
  bucket ? bucket.pre_created_engagement_id : undefined,
);

export const selectIsDisabledNotecard = createSelector(selectRootState, (bucket) =>
  bucket ? bucket[DISABLE_HANDWRITTEN_NOTE_STEP] : false,
);

export const selectSendOnHold = createSelector(selectRootState, (bucket) =>
  bucket && bucket[SEND_ON_HOLD] ? bucket[SEND_ON_HOLD] : '',
);

export const selectClaimPageTemplateId = createSelector(selectRootState, (bucket) => bucket[EXPERIENCE_ID]);

export const selectHasLegacyClaimPageConfig = createSelector(
  selectRootState,
  (bucket) => !!bucket[HAS_LEGACY_CLAIM_PAGE_CONFIG],
);

export const selectDelayedShippingExpirationDate = createSelector(selectRootState, (bucket) => {
  return bucket.delayed_shipping_expiration;
});

export const selectShippingInformation = createSelector(selectRootState, (bucket) =>
  bucket
    ? {
        ...bucket.receiverDetails,
        ...bucket.shippingDetails,
        shipping_option: bucket.shipping_option,
      }
    : undefined,
);

export const selectNoteInformation = createSelector(selectRootState, (bucket) => (bucket ? bucket.note : undefined));

export const selectAllowedDomains = createSelector(selectRootState, (bucket) =>
  bucket ? bucket[ALLOWED_DOMAINS] : [],
);

export const selectOLTitle = createSelector(selectRootState, (bucket) => (bucket ? bucket[TITLE] : ''));

export const select2FAEnabled = createSelector(selectRootState, (bucket) => bucket[ENABLED_2FA]);

export const select2FARecipientsList = createSelector(selectRootState, (bucket) =>
  bucket ? bucket[RECIPIENTS] : undefined,
);

export const selectEmailInformation = createSelector(selectRootState, (bucket) => (bucket ? bucket.email : undefined));

export const selectSelectedTemplate = createSelector(selectNoteInformation, (note) => note?.note_template_id);

export const selectEmailSelectedTemplate = createSelector(selectEmailInformation, (email) => email?.email_template_id);

export const selectIsNoteSkipped = createSelector(selectRootState, (bucket) => bucket.isNoteSkipped);

export const selectUserSenderInformation = createSelector(selectUserInfo, (userInfo) =>
  getSenderInfoObject(userInfo?.sender),
);

export const selectSOBEmail = createSelector(selectRootState, (bucket) =>
  bucket ? bucket[SEND_ON_BEHALF_THE_EMAIL] : undefined,
);

export const selectIsSOBEnabled = createSelector(selectUserEmail, selectSOBEmail, (userEmail, SOBEmail) => {
  if (!userEmail || !SOBEmail) {
    return false;
  }

  return userEmail !== SOBEmail;
});

export const selectSOBUser = createSelector(
  selectUsers,
  selectSOBEmail,
  selectIsSOBEnabled,
  (users, SOBEmail, isSOBEnabled) => {
    if (!isSOBEnabled || !SOBEmail || !users) {
      return null;
    }

    return users.find(({ email }) => email === SOBEmail);
  },
);

export const selectSOBUserRemainingBudget = createSelector(selectSOBUser, (user) =>
  user ? Number(user.available_budget) - Number(user.used_budget) : null,
);

export const selectIsSOBUserEmailIntegrationDisabled = createSelector(selectSOBUser, (user) =>
  user ? user?.integration.email_integration === OFF : undefined,
);

export const selectSenderInformation = createSelector(
  selectUserSenderInformation,
  selectSOBEmail,
  selectUsers,
  (userSenderInfo, SOBEmail, users) => {
    if (SOBEmail) {
      return users ? getSenderInfoObject(users.find(({ email }) => email === SOBEmail)) : undefined;
    }
    return userSenderInfo;
  },
);

export const selectBucketIsFetching = createSelector(selectRootState, (bucket) => (bucket ? bucket.isFetching : false));

export const selectBucketError = createSelector(selectRootState, (bucket) => (bucket ? bucket.error : undefined));

export const selectCRMRecordId = createSelector(selectShippingInformation, (shippingInfo) =>
  shippingInfo ? shippingInfo[RECEIVER_CRM_RECORD_ID] : null,
);

export const selectIsDigitalBucket = createSelector(selectBucketItems, (items: IInventoryWithType[] | undefined) =>
  items && items.length ? items.every(isItemDigital) : false,
);

export const selectIsDigitalPYGBucket = createSelector(
  selectPYGCampaignsList,
  (campaigns: ICampaign[] | undefined | null) =>
    campaigns && campaigns.length ? campaigns.every(isDigitalBox) : false,
);

export const selectIsBusinessReasonRequired = createSelector(
  selectRootState,
  selectIsDirectSendFlow,
  selectCurrentOrganizationOptions,
  (bucket, isDirectSend, orgOptions) => {
    const requiredByCampaign =
      bucket[PRE_CREATED_ENGAGEMENT_ID] !== CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID
        ? bucket[BUSINESS_REASON_REQUIRED]
        : false;
    const requiredByOrg = isDirectSend && !!orgOptions?.[REQUIRE_BUSINESS_REASON_FOR_DIRECT_SENDS];

    return requiredByCampaign || requiredByOrg;
  },
);

export const selectIsShippingOptionEnabled = createSelector(
  selectBucketItems,
  selectPYGCampaignsList,
  selectFulfillmentCenterItems,
  (
    bucketItems: (ICampaignItem | IOnDemandItem)[] | undefined,
    pickCampaigns: ICampaign[] | null | undefined,
    fcItems,
  ) =>
    !!(
      bucketItems &&
      bucketItems.length &&
      fcItems?.find((fcItem) => fcItem.uid === bucketItems[0].fulfillment_center_id)?.is_shipping_option_supported
    ) ||
    !!(
      pickCampaigns &&
      pickCampaigns.length &&
      pickCampaigns
        .map((campaign) => campaign.items)
        .flat()
        .some(
          (item) => fcItems?.find((fcItem) => fcItem.uid === item.fulfillment_center_id)?.is_shipping_option_supported,
        )
    ),
);

export const selectOnlyDigitalItems = createSelector(
  selectBucketItems,
  (items: (ICampaignItem | IOnDemandItem)[] | undefined) => (items && items.length ? items?.filter(isItemDigital) : []),
);

export const selectOnlyDigitalItemsNames = createSelector(
  selectOnlyDigitalItems,
  (items: (ICampaignItem | IOnDemandItem)[] | undefined) =>
    items && items.length ? items?.map((item) => item.name) : [],
);

export const selectTemplateValues = createSelector(
  selectShippingInformation,
  selectSenderInformation,
  selectOnlyDigitalItemsNames,
  selectCurrentSignature,
  selectSOBUser,
  selectCurrentOrganizationName,
  (shipping, sender, digitalNames, signature, SOBUser, currentOrgName) => ({
    ...(sender ? sender : {}),
    ...(shipping ? shipping : {}),
    [DIGITAL_GIFT_NAMES]: digitalNames,
    [SIGNATURE]: SOBUser ? SOBUser[SIGNATURE] : signature,
    [SENDER_COMPANY_NAME]: currentOrgName,
  }),
);

export const selectIsSkipCrmEnabledForCurrentUserInOrg = createSelector(
  selectIsCrmSkipAllowedToUser,
  selectIsSkipCrmAllowedInOrg,
  (isCrmSkippedForUser, isSkipCrmEnabledInOrg) => {
    return isCrmSkippedForUser && isSkipCrmEnabledInOrg;
  },
);

export const selectShippingValidationSchema = createSelector(
  selectIsCurrentOrganizationSupportedAnyCrm,
  selectIsDigitalBucket,
  selectIsDigitalPYGBucket,
  selectIsSkipCrmEnabledForCurrentUserInOrg,
  selectIsDirectSendFlow,
  (isSupportedCRM, isDigitalSend, isDigitalPYG, isCRMSkipAllowedForUser, isDirectSend) => {
    const hasCRM = Boolean(isSupportedCRM && !isCRMSkipAllowedForUser);
    const isDigital = isDigitalSend || isDigitalPYG;

    return getShippingValidationSchema({ isDigital, hasCRM, isDirectSend });
  },
);

export const selectShippingInitialValues = (isForcedDS: boolean) =>
  createSelector(
    selectContactDetails,
    selectShippingDetails,
    selectIsDigitalBucket,
    selectIsCurrentOrganizationSupportedAnyCrm,
    selectIsDirectSendFlow,
    (contactInfo, shippingDetails, isDigital, hasCRM = false, isDirectSend) => {
      const { receiver_address, ...restShippingDetails } = shippingDetails || {};

      const baseInitialValues = getShippingInitialValues({ isDigital, hasCRM, isForcedDS, isDirectSend });

      const initialAddress = isDigital ? undefined : receiver_address ?? INITIAL_SHIPPING_ADDRESS_FORM_STATE;

      return {
        ...baseInitialValues,
        ...contactInfo,
        receiver_address: initialAddress,
        ...restShippingDetails,
      };
    },
  );

export const selectOneLinkShippingData = createSelector(selectRootState, (bucket) => {
  const {
    expiration_date,
    disable_sender_email_notifications,
    receiver_reminder_interval,
    password,
    allowed_domains,
    max_submit_count,
    title,
    disable_recipient_shipped_email,
  } = bucket || {};

  return {
    password,
    ...(allowed_domains && !!allowed_domains.length ? { allowed_domains } : {}),
    expiration_date,
    disable_sender_email_notifications,
    receiver_reminder_interval,
    max_submit_count,
    title,
    disable_recipient_shipped_email,
  };
});

export const selectContactDetails = createSelector(selectRootState, (bucket) => bucket.receiverDetails);

export const selectShippingDetails = createSelector(selectRootState, (bucket) => bucket.shippingDetails);

export const selectOneLinkShippingDetails = createSelector(
  selectShippingDetails,
  ({ is_receiver_address_fixed, org_address_ids, receiver_address, is_office_dropship_enabled }) => ({
    is_receiver_address_fixed,
    org_address_ids,
    receiver_address,
    is_office_dropship_enabled,
  }),
);

export const selectReceiverAddress = createSelector(selectShippingDetails, (shippingDetails) => {
  return shippingDetails.receiver_address;
});

export const selectOneLinkShippingInitialValues = createSelector(
  selectOneLinkShippingData,
  selectIsDigitalBucket,
  (oneLinkShippingData, isDigital) => ({
    ...(isDigital ? INITIAL_ONE_LINK_DIGITAL_SHIPPING_FORM_STATE : INITIAL_ONE_LINK_PHYSICAL_SHIPPING_FORM_STATE),
    ...oneLinkShippingData,
  }),
);

export const selectOneLinkValidationSchema = createSelector(selectIsDigitalBucket, (isDigital) =>
  isDigital ? ONE_LINK_DIGITAL_SHIPPING_VALIDATION_SCHEMA : ONE_LINK_PHYSICAL_SHIPPING_VALIDATION_SCHEMA,
);

export const selectBucketBulk = createSelector(selectRootState, (bucket) => (bucket ? bucket.bulk : undefined));

export const selectIsBulkSubmitted = createSelector(selectBucketBulk, (bulk) => (bulk ? bulk.isSubmitted : false));

export const selectUploadedCSV = createSelector(selectBucketBulk, (bulk) => (bulk ? bulk.csv : null));

export const selectIsDisabledBulkShipmentTracking = createSelector(selectBucketBulk, (bulk) =>
  bulk ? bulk[DISABLE_SHIPMENT_TRACKING] : false,
);

export const selectIsDisabledSenderNotifications = createSelector(selectRootState, (bucket) =>
  bucket ? bucket[DISABLE_SENDER_EMAIL_NOTIFICATIONS] : false,
);

export const selectIsDisabledRecipientEmailNotifications = createSelector(selectRootState, (bucket) =>
  bucket ? bucket[DISABLE_RECIPIENT_SHIPPED_EMAIL] : false,
);

export const selectReminderInterval = createSelector(
  selectRootState,
  (bucket) => bucket && bucket[RECEIVER_REMINDER_INTERVAL],
);

export const selectBusinessReasonId = createSelector(selectRootState, (bucket) =>
  bucket && bucket[BUSINESS_REASON_ID] ? bucket[BUSINESS_REASON_ID] : '',
);
export const selectBucketCampaignName = createSelector(selectRootState, (bucket) => bucket.campaign_name || '');

const selectShippingOption = createSelector(selectRootState, (bucket) => bucket.shipping_option);

export const selectBulkResult = createSelector(selectBucketBulk, (bulk) => (bulk ? bulk.result : null));

export const selectBulkData = createSelector(
  selectUploadedCSV,
  selectPreCreatedBoxId,
  selectBucketItems,
  selectCurrentOrganizationId,
  selectIsDisabledSenderNotifications,
  selectIsDisabledBulkShipmentTracking,
  selectSendOnHold,
  selectNoteInformation,
  selectEmailInformation,
  selectPYGCampaignsIDsList,
  selectShippingOption,
  selectReminderInterval,
  selectBusinessReasonId,
  selectDelayedShippingExpirationDate,
  selectShippingDetails,
  selectBucketCampaignName,
  selectIsDisabledRecipientEmailNotifications,
  selectClaimPageTemplateId,
  (
    // tslint:disable:variable-name
    csv,
    pre_created_engagement_id,
    items,
    org_id,
    disable_sender_email_notifications,
    disable_shipment_tracking,
    on_hold_until,
    note,
    email,
    pick_campaign_ids,
    shipping_option,
    receiver_reminder_interval,
    business_reason_id,
    delayed_shipping_expiration,
    shippingDetails,
    campaign_name,
    disable_recipient_shipped_email,
    experience_id,
  ) => ({
    ...(csv ? { file_name: csv.name } : {}),
    pre_created_engagement_id,
    items,
    org_id,
    business_reason_id,
    disable_sender_email_notifications,
    disable_shipment_tracking,
    on_hold_until,
    shipping_option,
    ...(delayed_shipping_expiration ? { delayed_shipping_expiration } : {}),
    ...(note ? { note_template: note[MESSAGE] } : {}),
    ...(email
      ? {
          email_message: email[EMAIL_MESSAGE],
          email_subject: email[EMAIL_SUBJECT],
          email_template_id: email[EMAIL_TEMPLATE_ID],
        }
      : {}),
    ...(pick_campaign_ids ? { pick_campaign_ids } : {}),
    ...(!receiver_reminder_interval || receiver_reminder_interval === EngagementReminderIntervalsEnum.Custom
      ? {}
      : { receiver_reminder_interval }),
    ...shippingDetails,
    campaign_name,
    disable_recipient_shipped_email,
    ...(experience_id === undefined ? {} : { experience_id }),
  }),
);
// tslint:enable:variable-name

export const selectIsBulkLoading = createSelector(selectBucketBulk, (bulk) => (bulk ? bulk.isLoading : false));

export const selectIsBoxStepValid = (isBulk?: boolean) =>
  createSelector(
    selectIsPYGEnabled,
    selectPreCreatedBoxId,
    selectPYGCampaignsIDsList,
    selectBucketItems,
    (isPyg, boxId, PYGCampaignIDs, bucketItems) => {
      const isValidSingle = Boolean(isPyg ? PYGCampaignIDs?.length : bucketItems?.length);
      const isValidBulk = !!(isPyg ? PYGCampaignIDs?.length : boxId);

      return isBulk ? isValidSingle && isValidBulk : isValidSingle;
    },
  );

export const selectIsBulkShippingStepValid = createSelector(selectShippingDetails, (shipping) => {
  const { status, is_receiver_address_fixed, org_address_ids } = shipping || {};

  // check if Recipient Decides From List has at least 2 addresses, other combinations are valid
  if (is_receiver_address_fixed && status === AWAITING_ADDRESS && Array.isArray(org_address_ids)) {
    return org_address_ids.length > 1;
  }

  return true;
});

export const selectIsShippingStepValid = () =>
  createSelector(
    selectShippingInformation,
    selectShippingValidationSchema,
    selectIsOneLinkFlow,
    selectOneLinkShippingData,
    selectOneLinkValidationSchema,
    (shippingInfo, recipientValidationSchema, isOneLink, oneLinkShippingData, oneLinkValidationSchema) => {
      if (isOneLink) {
        return Boolean(oneLinkShippingData && oneLinkValidationSchema.isValidSync(oneLinkShippingData));
      }

      return Boolean(shippingInfo && recipientValidationSchema.isValidSync(shippingInfo));
    },
  );

export const selectIsNotesStepValid = () =>
  createSelector(selectNoteInformation, (note) => {
    const isNoteSkipped = note ? note[NOTE_TEMPLATE_ID] === null : false;
    return isNoteSkipped || (note ? !!(note[NOTE_TEMPLATE_ID] && note[MESSAGE]) : false);
  });

export const selectIsEmailStepValid = (isBulk: boolean) =>
  createSelector(selectEmailInformation, selectIsOneLinkFlow, (email, isOneLinkFlow) => {
    const isEmailSkipped = email ? email[EMAIL_TEMPLATE_ID] === null : false;

    const validSingle =
      isEmailSkipped ||
      (email
        ? !!(email[EMAIL_TEMPLATE_ID] && email[EMAIL_MESSAGE] && email[EMAIL_SUBJECT] && email[RECEIVER_EMAIL])
        : false);
    const validBulk =
      isEmailSkipped || (email ? !!(email[EMAIL_TEMPLATE_ID] && email[EMAIL_MESSAGE] && email[EMAIL_SUBJECT]) : false);

    // Validation conditions for Bulk and OneLink flows are the same
    return isBulk || isOneLinkFlow ? validBulk : validSingle;
  });

export const selectIsUploadStepValid = () => createSelector(selectUploadedCSV, Boolean);

export const selectIsClaimPageTemplateStepValid = () =>
  createSelector(selectClaimPageTemplateId, () => {
    // Optional step
    return true;
  });

export const selectInternalNotes = createSelector(selectRootState, (bucket) =>
  bucket && bucket[INTERNAL_NOTES] ? bucket[INTERNAL_NOTES] : '',
);

export const selectOneLinkCandidate = createSelector(
  selectIsDigitalBucket,
  selectOneLinkShippingData,
  selectContactDetails,
  selectShippingDetails,
  selectNoteInformation,
  selectEmailInformation,
  selectBucketItems,
  selectPreCreatedBoxId,
  selectInternalNotes,
  selectBusinessReasonId,
  selectSendOnHold,
  selectSOBEmail,
  selectPYGCampaignsList,
  selectReminderInterval,
  select2FARecipientsList,
  select2FAEnabled,
  selectIsDisabledSenderNotifications,
  selectShippingOption,
  selectOLTitle,
  selectBucketCampaignName,
  selectIsDisabledRecipientEmailNotifications,
  selectClaimPageTemplateId,
  // tslint:disable:variable-name
  (
    isDigital,
    oneLinkConfiguration,
    receiverDetails,
    shippingDetails,
    customMessage,
    emailMessage,
    items,
    pre_created_engagement_id,
    internal_notes,
    business_reason_id,
    on_hold_until,
    send_on_behalf_the_email,
    pick_campaigns,
    receiver_reminder_interval,
    recipients,
    enabled_2fa,
    disable_sender_email_notifications,
    shipping_option,
    title,
    campaign_name,
    disable_recipient_shipped_email,
    experience_id,
  ): IOneLinkEngagementCandidate => {
    // tslint:enable:variable-name

    const { expiration_date, ...rest } = oneLinkConfiguration;

    // @ts-ignore should we use other type for OLManager? TODO
    return {
      ...rest,
      ...receiverDetails,
      ...(!isDigital ? mapShippingDetailsToOneLink(shippingDetails) : {}),
      ...(expiration_date ? { expiration_date } : {}),
      ...(isDigital ? {} : { shipping_option }),
      recipients: enabled_2fa ? recipients?.successfulRecipients : undefined,
      enabled_2fa,
      ...(customMessage ? customMessage : {}),
      ...(emailMessage?.email_template_id ? emailMessage : {}),
      items,
      pre_created_engagement_id,
      internal_notes,
      business_reason_id,
      on_hold_until,
      ...(!receiver_reminder_interval || receiver_reminder_interval === EngagementReminderIntervalsEnum.Custom
        ? {}
        : { receiver_reminder_interval }),
      ...(send_on_behalf_the_email ? { send_on_behalf_the_email } : {}),
      ...(pick_campaigns ? { pick_campaigns } : {}),
      disable_sender_email_notifications,
      campaign_name,
      disable_recipient_shipped_email,
      ...(experience_id === undefined ? {} : { experience_id }),
    };
  },
);

export const selectEngagementCandidate = createSelector(
  selectIsDigitalBucket,
  selectIsDirectSendFlow,
  selectContactDetails,
  selectShippingDetails,
  selectNoteInformation,
  selectEmailInformation,
  selectBucketItems,
  selectPreCreatedBoxId,
  selectInternalNotes,
  selectBusinessReasonId,
  selectSendOnHold,
  selectSOBEmail,
  selectPYGCampaignsList,
  selectReminderInterval,
  selectDelayedShippingExpirationDate,
  selectIsDisabledSenderNotifications,
  selectShippingOption,
  selectBucketCampaignName,
  selectIsDisabledRecipientEmailNotifications,
  selectClaimPageTemplateId,
  (
    isDigital,
    isDirectSend,
    receiverDetails,
    shippingDetails,
    noteInformation,
    emailInformation,
    items,
    // tslint:disable:variable-name
    pre_created_engagement_id,
    internal_notes,
    business_reason_id,
    on_hold_until,
    send_on_behalf_the_email,
    pick_campaigns,
    receiver_reminder_interval,
    delayed_shipping_expiration,
    disable_sender_email_notifications,
    shipping_option,
    campaign_name,
    disable_recipient_shipped_email,
    experience_id,
  ): // tslint:enable:variable-name
  TEngagementCandidate => {
    const shippingInfo = (() => {
      if (isDigital && receiverDetails) {
        return new ShippingDigitalAddressCandidate(receiverDetails);
      }

      return {
        ...receiverDetails,
        ...mapShippingDetailsToSend(shippingDetails),
        shipping_option,
      };
    })();

    return {
      ...shippingInfo,
      ...(isDirectSend ? { engagement_source_type: EngagementSourceTypesEnum.DirectSend } : {}),
      ...(noteInformation ? noteInformation : {}),
      ...(emailInformation?.email_template_id ? emailInformation : {}),
      items,
      pre_created_engagement_id,
      internal_notes,
      business_reason_id,
      on_hold_until,
      delayed_shipping_expiration,
      ...(!receiver_reminder_interval || receiver_reminder_interval === EngagementReminderIntervalsEnum.Custom
        ? {}
        : { receiver_reminder_interval }),
      ...(send_on_behalf_the_email ? { send_on_behalf_the_email } : {}),
      ...(pick_campaigns ? { pick_campaigns } : {}),
      disable_sender_email_notifications,
      campaign_name,
      disable_recipient_shipped_email,
      ...(experience_id === undefined ? {} : { experience_id }),
    };
  },
);

export const selectCandidateInstance = createSelector(
  selectIsOneLinkFlow,
  (state: IAppState) => state,
  (isOneLink, state) => (isOneLink ? selectOneLinkCandidate(state) : selectEngagementCandidate(state)),
);

export const selectReceiverEmail = createSelector(selectShippingInformation, (shipping) =>
  shipping ? shipping[RECEIVER_EMAIL] : undefined,
);

export const selectIsDelayedShipping = createSelector(
  selectShippingInformation,
  (shipping) => shipping?.[STATUS] === AWAITING_ADDRESS,
);

export const selectFlowValidation = (isBulk: boolean) =>
  createSelector(
    [
      selectIsBoxStepValid(isBulk),
      selectIsShippingStepValid(),
      selectIsNotesStepValid(),
      selectIsEmailStepValid(isBulk),
      selectIsUploadStepValid(),
      selectIsClaimPageTemplateStepValid(),
    ],
    (box, shipping, note, email, upload, claimPageTemplate) => ({
      box,
      shipping,
      note,
      email,
      upload,
      'claim-page-template': claimPageTemplate,
      summary: box && shipping && note && email,
    }),
  );

export const selectIsReceiverReminderSettingEnabled = createSelector(
  [selectIsDelayedShipping, selectIsDigitalBucket],
  (isDelayedShipping, isDigital) => {
    return isDelayedShipping || isDigital;
  },
);
